/* eslint-disable no-use-before-define */
import { Box } from '@mantine/core';
import * as React from 'react';

type Props = {
	/** Callback triggered when the caption is blurred */
	onBlur: (event: React.FocusEvent<HTMLParagraphElement>) => void;
	/** Callback triggered when keyboard is used within the caption */
	onKeyDown: (event: React.KeyboardEvent<HTMLParagraphElement>) => void;
	/** Whether the parent node is selected */
	isSelected: boolean;
	/** The caption text */
	caption: string;
	/** Placeholder text to display when the caption is empty */
	placeholder: string;
	/** Width of the caption */
	width: number;
	/** Additional CSS styles to apply to the caption */
	style?: React.CSSProperties;
};

/**
 * A component that renders a caption for an image or video.
 */
function Caption({ placeholder, caption, isSelected, width, ...rest }: Props) {
	const handlePaste = (event: React.ClipboardEvent<HTMLParagraphElement>) => {
		event.preventDefault();
		const text = event.clipboardData.getData('text/plain');
		window.document.execCommand('insertText', false, text);
	};

	const handleMouseDown = (ev: React.MouseEvent<HTMLParagraphElement>) => {
		// always prevent clicks in caption from bubbling to the editor
		ev.stopPropagation();
	};

	return (
		<CaptionContent
			width={width}
			isSelected={isSelected}
			onMouseDown={handleMouseDown}
			onPaste={handlePaste}
			className="caption"
			tabIndex={-1}
			role="textbox"
			contentEditable
			suppressContentEditableWarning
			data-caption={placeholder}
			{...rest}
		>
			{caption}
		</CaptionContent>
	);
}

function CaptionContent({
	children,
	width,
	isSelected,
	...props
}: {
	children: React.ReactNode;
	width: number;
	isSelected: boolean;
} & React.HTMLAttributes<HTMLDivElement>) {
	return (
		<Box
			component="p"
			sx={(theme) => ({
				cursor: 'text',
				width: width,
				maxWidth: '100%',

				'&:empty:not(:focus)': {
					display: isSelected ? 'block' : 'none',
				},

				'&:empty::before': {
					color: theme.other.getColor('text/secondary/default'),
					content: 'attr(data-caption)',
					pointerEvents: 'none',

					'@media print': {
						display: 'none',
					},
				},
			})}
			{...props}
		>
			{children}
		</Box>
	);
}

export default Caption;
